import { UPLOAD_STATUS } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect } from 'react';
import { convertToArray } from '../../../lib/uploadStatusModal';
import type { IProps } from './uploadStatusModal';

/**
 * Feeds the studies list upload status modal from the study's uploadStatus payload. Whenever that payload is non-empty it converts it to an array of stages, reads the overall percentage and remaining time, and locates the index of the first stage marked UPLOADING as the current step.
 *
 * @param study - Study whose uploadStatus drives the progress display.
 * @returns Upload progress state and its merging setter.
 */
export const useUploadStatusModal = ({ study }: Pick<IProps, 'study'>) => {
  const [state, setState] = useMergeState<{
    dataUpload: any[];
    dataRender: any[];
    percentage: number;
    aboutTimeLeftTotal: number;
    currentStep: number;
  }>({
    dataUpload: [],
    dataRender: [],
    percentage: 0,
    aboutTimeLeftTotal: 0,
    currentStep: 0,
  });

  useEffect(() => {
    if (!_.isEmpty(study?.uploadStatus)) {
      const dataUpload = convertToArray(study.uploadStatus);
      const aboutTimeLeftTotal = study.uploadStatus.aboutTimeLeft;
      const { percentage } = study.uploadStatus;
      /**
       * Holds the index of the upload stage currently in progress. Finds the first entry of dataUpload whose status is UPLOADING, yielding -1 when none is.
       *
       * @returns The zero-based index of the uploading stage, or -1.
       */
      const currentStep = _.findIndex(
        dataUpload,
        (item) => item.status === UPLOAD_STATUS.UPLOADING,
      );
      setState({
        dataUpload,
        percentage,
        aboutTimeLeftTotal,
        currentStep,
      });
    }
  }, [study?.uploadStatus]);

  return { state, setState };
};
